import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Base shimmer primitive — every skeleton below is built from this. Reuses
 * the app's existing neutral surface color (bg-tg-secondary-bg, the same
 * color real cards/rows sit on) so skeletons look like a natural part of the
 * theme rather than a foreign gray box. `animate-pulse` is a Tailwind core
 * utility — no config changes needed.
 */
export default function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-tg-secondary-bg ${className}`} />
}

/**
 * Wraps skeleton content in a proper loading landmark: `role="status"` +
 * `aria-live="polite"` announces "Loading…" to screen readers, and the
 * visually-hidden text keeps the DOM text content identical to the old
 * plain-text loading screens (see App.test.tsx's `getByText(/Loading/i)`).
 */
function StatusWrapper({ children, className = '' }: { children: ReactNode; className?: string }) {
  const { t } = useTranslation()
  return (
    <div role="status" aria-live="polite" className={className}>
      {children}
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  )
}

/**
 * Matches the real SwipeCard's silhouette (see components/SwipeCard.tsx):
 * a full-bleed rounded card with a bottom-aligned name/bio shimmer. Used by
 * both SwipePage's card-loading state and the top-level app boot skeleton.
 */
export function SwipeCardSkeleton() {
  return (
    <StatusWrapper className="relative h-full overflow-hidden rounded-2xl bg-tg-secondary-bg shadow-md">
      <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-2 px-5 py-4">
        <Skeleton className="h-5 w-2/5 bg-tg-hint/30" />
        <Skeleton className="h-3 w-1/4 bg-tg-hint/20" />
        <Skeleton className="h-3 w-3/5 bg-tg-hint/20" />
      </div>
    </StatusWrapper>
  )
}

/**
 * Top-level boot skeleton (App.tsx, while GET /me is in flight). We don't
 * yet know whether the user lands on /swipe, /photos, or /onboarding, so
 * this reuses the swipe-card silhouette — the most common landing state for
 * a returning user — plus two button-shaped bars mirroring the Skip/Like row.
 */
export function AppShellSkeleton() {
  return (
    <div className="flex min-h-dvh flex-col bg-tg-bg px-4 py-6">
      <div className="flex flex-1 flex-col">
        <SwipeCardSkeleton />
        <div className="mt-6 flex gap-4">
          <Skeleton className="h-14 flex-1 rounded-xl" />
          <Skeleton className="h-14 flex-1 rounded-xl" />
        </div>
      </div>
    </div>
  )
}

/**
 * Matches a real match row's shape (see pages/MatchesPage.tsx): a circular
 * avatar plus a name bar and a shorter last-message bar.
 */
function MatchRowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="h-3 w-3/5" />
      </div>
    </div>
  )
}

/** MatchesPage's loading state: a handful of repeated MatchRowSkeleton rows. */
export function MatchesSkeleton() {
  return (
    <StatusWrapper className="flex-1 divide-y divide-tg-hint/10">
      {Array.from({ length: 5 }, (_, i) => (
        <MatchRowSkeleton key={i} />
      ))}
    </StatusWrapper>
  )
}


/**
 * ProfilePage's loading state: header bar, avatar + name/city, action
 * buttons, and one wallet-card shimmer — matches the real page's shape
 * (see pages/ProfilePage.tsx) closely enough that the layout doesn't jump
 * once the fetch resolves.
 */
export function ProfileSkeleton() {
  return (
    <StatusWrapper className="flex min-h-dvh flex-col bg-tg-bg">
      <div className="flex items-center gap-3 px-4 pt-3">
        <Skeleton className="h-4 w-12" />
      </div>
      <div className="flex flex-col items-center gap-3 px-6 py-6">
        <Skeleton className="h-24 w-24 rounded-full" />
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-3 w-20" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-32 rounded-xl" />
          <Skeleton className="h-9 w-32 rounded-xl" />
        </div>
      </div>
      <div className="px-4">
        <Skeleton className="h-28 w-28 rounded-xl" />
      </div>
    </StatusWrapper>
  )
}

